import { Link } from "react-router-dom";

/** Pick a colour class based on the `onLight` prop: true → dark text on light bg,
 *  false → light text on dark bg, null/undefined → auto (theme-aware). */
function pickClass(onLight, classes) {
  if (onLight === true) return classes.dark;
  if (onLight === false) return classes.light;
  return classes.auto;
}

/**
 * Official InfiniteSkills logo.
 * Uses the uploaded PNG at /brand/logo.png (transparent background).
 * - variant="mark"  → logo only
 * - variant="full"  → logo + wordmark + subtitle
 * - `size` controls the pixel height of the mark; the wordmark scales with it.
 */
export function Logo({
  variant = "full",
  size = 36,
  className = "",
  onLight = null,   // force wordmark color on/off dark: pass true/false to override auto
  linkTo = "/",
  showSubtitle = true,
}) {
  const wordClass = pickClass(onLight, {
    dark: "text-[#0B1B3A]",
    light: "text-white",
    auto: "text-[#0B1B3A] dark:text-white",
  });
  const subClass = pickClass(onLight, {
    dark: "text-[#0B1B3A]/60",
    light: "text-white/70",
    auto: "text-muted-foreground",
  });

  const inner = (
    <span className={`inline-flex items-center gap-2.5 group ${className}`} data-testid="brand-logo">
      <img
        src="/brand/logo.png"
        alt="InfiniteSkills logo"
        width={size}
        height={size}
        style={{ height: size, width: "auto" }}
        className="object-contain flex-shrink-0 drop-shadow-[0_2px_8px_rgba(30,58,138,0.15)]"
      />
      {variant === "full" && (
        <span className="flex flex-col leading-tight">
          <span className={`font-display font-bold tracking-tight ${wordClass}`}
                style={{ fontSize: Math.max(14, size * 0.42) }}>
            InfiniteSkills
          </span>
          {showSubtitle && (
            <span className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${subClass}`}>
              Learning Portal
            </span>
          )}
        </span>
      )}
    </span>
  );

  return linkTo ? (
    <Link to={linkTo} aria-label="InfiniteSkills Learning Portal" className="inline-flex">
      {inner}
    </Link>
  ) : (
    inner
  );
}
